import { Vec3, vec3 } from '../core/math/vec3'
import { PartCatalog, partDef } from '../core/vessel/catalog'
import { PartCategory } from '../core/vessel/part'
import { Design } from '../editor/design'
import { Indicatori } from '../editor/indicators'
import { VoceElenco } from '../editor/storage'
import { massa, velocita, tempo, distanza } from './format'

const NOMI_CATEGORIA: Partial<Record<PartCategory, string>> = {
  command: 'Comando',
  tank: 'Serbatoi',
  engine: 'Motori',
  decoupler: 'Separazione',
  aero: 'Aerodinamica',
}

const ORDINE: PartCategory[] = ['command', 'tank', 'engine', 'decoupler', 'aero']

export interface AzioniOfficina {
  prendi(defId: string | null): void
  simmetria(n: number): void
  sposta(offset: Vec3): void
  ruota(asse: Vec3, angolo: number): void
  stadio(delta: number): void
  elimina(): void
  lancia(): void
  salva(): void
  carica(nome: string): void
  nuovo(): void
}

export interface StatoOfficina {
  design: Design
  indicatori: Indicatori
  selezione: string | null
  inMano: string | null
  simmetria: number
  salvati: VoceElenco[]
  messaggio: string | null
}

export interface Officina {
  readonly root: HTMLElement
  aggiorna(stato: StatoOfficina): void
}

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K, className?: string, testo?: string,
): HTMLElementTagNameMap[K] => {
  const n = document.createElement(tag)
  if (className) n.className = className
  if (testo !== undefined) n.textContent = testo
  return n
}

export function createOfficina(
  root: HTMLElement, catalog: PartCatalog, azioni: AzioniOfficina,
): Officina {
  root.innerHTML = ''
  root.className = 'officina'

  // ── barra in alto
  const barra = el('div', 'barra-alto')
  const titolo = el('span', 'titolo', 'Officina')
  const nome = el('span', 'nome-progetto')
  const spazio = el('span', 'spazio')

  const selettoreSimmetria = el('div', 'simmetria')
  selettoreSimmetria.append(el('span', 'etichetta', 'simmetria'))
  const bottoniSimmetria = new Map<number, HTMLButtonElement>()
  for (const n of [1, 2, 3, 4, 6]) {
    const b = el('button', 'sim', `${n}×`)
    b.onclick = () => azioni.simmetria(n)
    bottoniSimmetria.set(n, b)
    selettoreSimmetria.appendChild(b)
  }

  const bNuovo = el('button', 'azione', 'Nuovo')
  bNuovo.onclick = () => azioni.nuovo()
  const bSalva = el('button', 'azione', 'Salva')
  bSalva.onclick = () => azioni.salva()
  const elencoSalvati = el('select', 'azione')
  elencoSalvati.onchange = () => { if (elencoSalvati.value) azioni.carica(elencoSalvati.value) }
  const bLancia = el('button', 'azione lancia', 'LANCIA')
  bLancia.onclick = () => azioni.lancia()

  barra.append(titolo, nome, spazio, selettoreSimmetria, bNuovo, bSalva, elencoSalvati, bLancia)

  // ── catalogo a sinistra
  const catalogo = el('div', 'pannello catalogo')
  catalogo.appendChild(el('h2', '', 'Catalogo'))
  const bottoniParte = new Map<string, HTMLButtonElement>()
  for (const cat of ORDINE) {
    const parti = Object.values(catalog).filter((d) => d.category === cat)
    if (parti.length === 0) continue
    catalogo.appendChild(el('h3', '', NOMI_CATEGORIA[cat] ?? cat))
    for (const d of parti) {
      const b = el('button', 'parte')
      b.append(el('span', 'nome', d.name), el('span', 'peso', massa(d.dryMass + (d.tank?.propellantMass ?? 0))))
      b.onclick = () => azioni.prendi(d.id)
      bottoniParte.set(d.id, b)
      catalogo.appendChild(b)
    }
  }

  // ── ispettore della selezione
  const ispettore = el('div', 'pannello ispettore')
  ispettore.appendChild(el('h2', '', 'Parte selezionata'))
  const nomeSelezione = el('p', 'selezione-nome', 'nessuna')
  const comandi = el('div', 'comandi-parte')
  ispettore.append(nomeSelezione, comandi)

  const passoLineare = 0.05
  const passoAngolare = (5 * Math.PI) / 180
  const spostamenti: Array<[string, Vec3]> = [
    ['← X', vec3(-passoLineare, 0, 0)], ['→ X', vec3(passoLineare, 0, 0)],
    ['← Z', vec3(0, 0, -passoLineare)], ['→ Z', vec3(0, 0, passoLineare)],
    ['↓ Y', vec3(0, -passoLineare, 0)], ['↑ Y', vec3(0, passoLineare, 0)],
  ]
  for (const [etichetta, off] of spostamenti) {
    const b = el('button', 'micro', etichetta)
    b.onclick = () => azioni.sposta(off)
    comandi.appendChild(b)
  }
  for (const [etichetta, asse, segno] of [['↺ inclina', vec3(0, 0, 1), 1], ['↻ inclina', vec3(0, 0, 1), -1]] as const) {
    const b = el('button', 'micro', etichetta)
    b.onclick = () => azioni.ruota(asse, passoAngolare * segno)
    comandi.appendChild(b)
  }
  const bStadioSu = el('button', 'micro', 'stadio +')
  bStadioSu.onclick = () => azioni.stadio(1)
  const bStadioGiu = el('button', 'micro', 'stadio −')
  bStadioGiu.onclick = () => azioni.stadio(-1)
  const bElimina = el('button', 'micro elimina', 'Elimina')
  bElimina.onclick = () => azioni.elimina()
  comandi.append(bStadioGiu, bStadioSu, bElimina)

  const scostamento = el('p', 'scostamento')
  const istruzioni = el('p', 'istruzioni')
  ispettore.append(scostamento, istruzioni)

  // ── letture e avvisi
  const letture = el('div', 'pannello letture')
  letture.appendChild(el('h2', '', 'Il razzo'))
  const griglia = el('dl', 'valori')
  const celle = new Map<string, HTMLElement>()
  for (const [campo, etichetta] of [
    ['massa', 'massa al decollo'], ['dv', 'Δv totale'], ['twr', 'spinta/peso'],
    ['stabilita', 'stabilità'], ['coppia', 'coppia residua'], ['parti', 'parti'],
  ] as const) {
    const dt = el('dt', '', etichetta)
    const dd = el('dd')
    dd.dataset.campo = campo
    dd.textContent = '—'
    griglia.append(dt, dd)
    celle.set(campo, dd)
  }
  const stadi = el('div', 'stadi')
  const avvisi = el('div', 'segnalazioni')
  letture.append(griglia, el('h3', '', 'Stadi'), stadi, avvisi)

  const messaggio = el('div', 'messaggio-officina')
  messaggio.hidden = true

  // catalogo e ispettore stanno nella stessa colonna: l'ispettore ancorato in
  // basso copriva la coda del catalogo, e le parti in fondo erano irraggiungibili
  const colonna = el('div', 'colonna-sinistra')
  colonna.append(catalogo, ispettore)
  root.append(barra, colonna, letture, messaggio)

  return {
    root,
    aggiorna(s) {
      nome.textContent = s.design.nome

      for (const [n, b] of bottoniSimmetria) b.classList.toggle('attivo', n === s.simmetria)
      for (const [id, b] of bottoniParte) b.classList.toggle('in-mano', id === s.inMano)

      const scelta = s.selezione
        ? s.design.vessel.parts.find((p) => p.id === s.selezione) ?? null
        : null
      nomeSelezione.textContent = scelta
        ? `${partDef(catalog, scelta.defId).name} — stadio ${scelta.stage}`
        : (s.inMano ? `in mano: ${partDef(catalog, s.inMano).name}` : 'nessuna')
      comandi.style.display = scelta ? '' : 'none'
      scostamento.textContent = scelta
        ? `scostamento dall’asse: ${(Math.hypot(scelta.position.x, scelta.position.z) * 100).toFixed(0)} cm`
        : ''

      // l'officina non e ovvia al primo colpo: qui si dice cosa fare adesso
      istruzioni.innerHTML = scelta
        ? 'Spostala coi comandi qui sopra e guarda la <b>coppia residua</b> a destra. <b>Canc</b> per toglierla.'
        : s.inMano
          ? 'Clicca un <b>pallino verde</b> per agganciarla in linea, oppure il <b>fianco</b> di un serbatoio per montarla di lato.'
          : 'Scegli una parte dal catalogo per aggiungerla, oppure clicca il razzo per selezionarne una. Trascina per girare, rotella per lo zoom.'

      const i = s.indicatori
      const primo = i.stadi.find((x) => x.thrustSeaLevel > 0)
      celle.get('massa')!.textContent = massa(i.massa)
      celle.get('dv')!.textContent = `${Math.round(i.deltaVTotale)} m/s`
      celle.get('dv')!.className = i.deltaVTotale >= 4500 ? 'buono' : 'allarme'
      celle.get('twr')!.textContent = primo ? primo.twrSeaLevel.toFixed(2) : '—'
      celle.get('twr')!.className = primo && primo.twrSeaLevel >= 1.2 ? 'buono' : 'allarme'
      celle.get('stabilita')!.textContent = i.cop ? `${i.margineStabilita.toFixed(2)} cal` : '—'
      celle.get('stabilita')!.className = i.margineStabilita >= 0 ? 'buono' : 'attenzione'
      celle.get('coppia')!.textContent = `${Math.round(i.coppiaResidua).toLocaleString('it-IT')} N·m`
      celle.get('coppia')!.className = i.coppiaResidua > i.coppiaDisponibile
        ? 'allarme' : i.coppiaResidua > i.coppiaDisponibile * 0.02 ? 'attenzione' : ''
      celle.get('parti')!.textContent = String(s.design.vessel.parts.length)

      stadi.innerHTML = ''
      for (const st of i.stadi) {
        const riga = el('div', 'stadio')
        riga.append(
          el('span', 'n', `${st.stage}`),
          el('span', 'dv', `${Math.round(st.deltaV)} m/s`),
          el('span', 'bt', `${Math.round(st.burnTime)} s`),
        )
        stadi.appendChild(riga)
      }

      avvisi.innerHTML = ''
      for (const a of i.avvisi) {
        avvisi.appendChild(el('p', `segnalazione ${a.gravita}`, a.testo))
      }

      bLancia.disabled = i.avvisi.some((a) => a.gravita === 'errore')
      bLancia.title = bLancia.disabled ? 'Risolvi prima gli errori elencati' : 'Porta il razzo sulla rampa'

      elencoSalvati.innerHTML = ''
      const vuoto = el('option', '', s.salvati.length ? 'carica…' : 'nessun progetto salvato')
      vuoto.value = ''
      elencoSalvati.appendChild(vuoto)
      for (const v of s.salvati) {
        const o = el('option', '', v.nome)
        o.value = v.nome
        elencoSalvati.appendChild(o)
      }

      messaggio.hidden = !s.messaggio
      if (s.messaggio) messaggio.textContent = s.messaggio
    },
  }
}
